import "./style.scss";

import { Avatar } from "@mui/material";
import AlertTriangle from "assets/img/svg/alertTriangle";
import Plus from "assets/img/svg/plus";
import DocumentationLink from "components/shared/DocumentationLink";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import InfoTooltip from "components/shared/InfoTooltip";
import MessageBlock from "components/shared/MessageBlock";
import PeopleAutocompleteField from "components/shared/PeopleAutocompleteField";
import RoundBorderChip from "components/shared/RoundBorderChip";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import TitleAutocompleteField from "components/shared/TitleAutocompleteField";
import WorkSelectField from "components/TitleFormContainer/WorkSelectField";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import type { IWorkTitleSummary } from "containers/Works/types";
import { formatLocaleDateString, getCleanStartorEndDay } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ChangeEvent, MutableRefObject } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TPeriod } from "store/periodStore";
import { fetchPeriodTypes, PeriodType } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";

import TermCustomFields from "../TermCustomFields";

import variables from "../../../assets/styles/_variables.module.scss";
import type { ITermFormData, ITermMessagesOptions } from "..";
import CoveredTitlesField from "../CoveredTitlesField";
import TermWarningMessage from "../TermWarningMessage";

interface NewTermData {
    /**
     * Data and functions from the useForm hook needed in this component.
     */
    useFormData: Omit<ITermFormData, "dirtyFields">;
    /**
     * List of all representees for the beneficiary selected
     */
    associatedPersons: IRepresentative[];
    /**
     * Beneficiary selected (from form).
     */
    chosenBeneficiary: IBeneficiar | null;
    /**
     * Representee selected (from form).
     */
    chosenRepresentative: IRepresentative | null;
    /**
     * All beneficiaries available for the autocomplete.
     */
    beneficiarsAutocomplete: IBeneficiar[];
    /**
     * index of step in steps array.
     */
    indexStep: number;
    /**
     * title of steps array
     */
    stepTitle: string;
    /**
     * index of step in steps array
     */
    stepSubtitle: string;
    /**
     * Whether the form is for a new term or edit.
     */
    mode: "edit" | "new";
    currentTitleId?: number | null;
    currentWorkId?: number;
    currentBeneficiaryId?: number;
    isBookPublisher: boolean;
    triggerSaveButton: any;
    /**
     * Values for displaying warning messages.
     */
    otherTermsMessage: ITermMessagesOptions;
    setValue?: ITermFormData["setValue"];
    currentCoveredTitleIds?: number[];
    workTitles?: IWorkTitleSummary[];
    workTitlesLoading?: boolean;
    coveredTitlesHydratedWorkIdRef?: MutableRefObject<number | null>;
}

/**
 * This component contains all fields for the first section of the term form.
 */
const NewTermStep = ({
    useFormData,
    associatedPersons,
    chosenRepresentative,
    beneficiarsAutocomplete,
    indexStep,
    stepTitle,
    stepSubtitle,
    chosenBeneficiary,
    mode,
    currentTitleId,
    currentWorkId,
    currentBeneficiaryId,
    isBookPublisher,
    triggerSaveButton,
    otherTermsMessage,
    setValue,
    currentCoveredTitleIds,
    workTitles: workTitlesFromParent = [],
    workTitlesLoading = false,
    coveredTitlesHydratedWorkIdRef,
}: NewTermData) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { periodTypes, periods, activePeriod } = useAppSelector((state) => state.period);
    const { control, errors, watch } = useFormData;
    const { getCompanySettings } = useCompanySettings();
    const usesMultiPeriods = getCompanySettings("uses_simultaneous_period");

    const chosenTitle = watch("title");
    const chosenWork = watch("work");
    const linkedResource = isBookPublisher ? chosenWork : chosenTitle;
    const workTitles = workTitlesLoading
        ? []
        : workTitlesFromParent.length
          ? workTitlesFromParent
          : chosenWork?.titles ?? [];
    // Wait for a known count. Showing the field while titles load flashes it on works with fewer than two articles.
    const showCoveredTitlePicker =
        isBookPublisher && Boolean(chosenWork) && !workTitlesLoading && workTitles.length > 1;

    useEffect(() => {
        if (!setValue || !isBookPublisher || !coveredTitlesHydratedWorkIdRef) return;
        if (!chosenWork) {
            coveredTitlesHydratedWorkIdRef.current = null;
            setValue("coveredTitles", []);
            return;
        }
        if (coveredTitlesHydratedWorkIdRef.current === chosenWork.id) return;
        if (currentCoveredTitleIds?.length && workTitles.length) {
            const byId = new Map(workTitles.map((title) => [title.id, title]));
            setValue(
                "coveredTitles",
                currentCoveredTitleIds.map((id) => byId.get(id)).filter((title): title is IWorkTitleSummary => !!title),
            );
            coveredTitlesHydratedWorkIdRef.current = chosenWork.id;
            return;
        }
        if (mode === "new") {
            if (coveredTitlesHydratedWorkIdRef.current != null) {
                setValue("coveredTitles", []);
            }
            coveredTitlesHydratedWorkIdRef.current = chosenWork.id;
        }
    }, [
        chosenWork?.id,
        workTitles,
        currentCoveredTitleIds,
        isBookPublisher,
        mode,
        setValue,
        coveredTitlesHydratedWorkIdRef,
    ]);

    useEffect(() => {
        if (usesMultiPeriods) dispatch(fetchPeriodTypes());
    }, []);

    /**
     * Format period options and indicate if chosen title exists on other period.
     */
    const periodOptions: (TPeriod & { titleExists?: boolean })[] = useMemo(() => {
        if (!activePeriod || !linkedResource || !usesMultiPeriods) return [];
        return periods
            .filter((item) => item.isActive)
            .map((period) => {
                const resourceExistsOnPeriod = otherTermsMessage.titlePeriods.find((item) => {
                    if (period.id === activePeriod) return false;
                    return isBookPublisher ? item.workId === linkedResource.id : item.titleId === linkedResource.id;
                });
                if (resourceExistsOnPeriod) {
                    return { ...period, titleExists: true };
                } else {
                    return period;
                }
            });
    }, [periods, activePeriod, linkedResource, isBookPublisher, otherTermsMessage, usesMultiPeriods]);

    const [filterValue, setFilterValue] = useState("");

    const filteredRepresentatives = useMemo(() => {
        return associatedPersons?.filter((people) => {
            if (filterValue !== "") {
                return (
                    people.name?.toLowerCase().includes(filterValue) ||
                    people.surname?.toLowerCase().includes(filterValue)
                );
            } else {
                return associatedPersons;
            }
        });
    }, [filterValue, associatedPersons]);

    const handleRepresentativeChange = (e: ChangeEvent<HTMLInputElement>) => setFilterValue(e.target.value);

    const renderInputRepresentative = (params: any) => (
        <StyledTextField
            {...params}
            error={!!errors.representative}
            helperText={errors.representative?.message ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleRepresentativeChange(e)}
        />
    );
    const renderInputPeriodType = (params: any) => (
        <StyledTextField {...params} error={!!errors.periodType} helperText={errors.periodType?.message ?? ""} />
    );

    const renderOptionTextRepresentative = (option: any) =>
        `${(option?.surname ?? "").toUpperCase()} ${option?.name ?? ""}`;
    const renderOptionTextPeriodType = (option: TPeriod) => {
        if (option && option.periodDuration) {
            return `${option.periodDuration ?? ""} ${t("ConnectTitle.PeriodTypeMonth")} - ${formatLocaleDateString({
                date: option.periodStart,
            })} ↔︎ ${formatLocaleDateString({ date: option.periodEnd })}`;
        } else {
            return "";
        }
    };

    const renderOptionRepresentative = (props: any, option: any) => {
        return option.addElement ? (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li
                {...props}
                key={option.id}
                className={`autocomplete-beneficiaries${option.id === chosenRepresentative?.id ? " selected" : ""}`}
            >
                <div className="autocomplete-beneficiaries-left-content">
                    <Avatar
                        src={option.avatarUrl}
                        variant="circular"
                        sx={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#EEF3FD",
                            color: variables["foreground"],
                            fontSize: "14px",
                        }}
                    >
                        {option.surname ? option.surname?.slice(0, 1) : option.name?.slice(0, 1)}
                    </Avatar>
                    <span>
                        {option.surname.toUpperCase()} {option.name ?? ""}
                    </span>
                </div>
            </li>
        );
    };

    const renderOptionPeriodType = (props, option: TPeriod & { titleExists?: boolean }) => {
        return (
            <li {...props} key={option.id}>
                <div className="term-form-period-option">
                    <span>
                        {option.periodDuration ?? ""} {t("ConnectTitle.PeriodTypeMonth")} -{" "}
                        {formatLocaleDateString({
                            date: getCleanStartorEndDay(option.periodStart),
                            options: { timeZone: null },
                        })}{" "}
                        {"↔︎"}{" "}
                        {formatLocaleDateString({
                            date: getCleanStartorEndDay(option.periodEnd),
                            options: { timeZone: null },
                        })}
                    </span>
                    {option.titleExists && (
                        <RoundBorderChip
                            text={
                                isBookPublisher
                                    ? t("ConnectTitle.WorkPresentPeriod")
                                    : t("ConnectTitle.TitlePresentPeriod")
                            }
                            color="blue"
                            tooltipText={
                                isBookPublisher
                                    ? t("ConnectTitle.WorkPresentPeriodTooltip")
                                    : t("ConnectTitle.TitlePresentPeriodTooltip")
                            }
                        />
                    )}
                </div>
            </li>
        );
    };

    const handleRepresentativeClear = (reason: string, value: string) => {
        if (mode === "edit") {
            if (reason === "clear" || value === "") triggerSaveButton();
        }
    };

    return (
        <div className="new-term-first-step">
            <h3 className="form-section-title first">
                {" "}
                {indexStep + 1}. {stepTitle}
                {mode === "edit" && (
                    <InfoTooltip
                        text={t("TransferTerms.Helpdesk.AccessDoc")}
                        link={"https://helpdesk.crealo.app/fr/article/modifier-un-contrat-189yn3s/"}
                        iconType="question"
                    />
                )}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                {showCoveredTitlePicker && (
                    <CoveredTitlesField
                        useFormData={{ control, errors, watch, setValue }}
                        work={chosenWork}
                        workTitles={workTitles}
                        loading={workTitlesLoading}
                        startsAsException={Boolean(currentCoveredTitleIds?.length)}
                    />
                )}
                {isBookPublisher ? (
                    <WorkSelectField
                        control={control}
                        error={errors.work}
                        required
                        disabled={mode === "edit" || !!currentWorkId}
                        extraOption={chosenWork}
                    />
                ) : (
                    <TitleAutocompleteField
                        disabled={mode === "edit" || !!currentTitleId}
                        errors={errors.title}
                        control={control}
                        controlName="title"
                        chosenTitle={chosenTitle}
                        required
                    />
                )}
                <PeopleAutocompleteField
                    data={beneficiarsAutocomplete}
                    type="beneficiaries"
                    label={t("ConnectTitle.LinkedBeneficiary")}
                    control={control}
                    controlName="beneficiary"
                    errors={errors.beneficiary}
                    watch={watch}
                    required
                    disabled={mode === "edit" || !!currentBeneficiaryId}
                    customRequiredMessage={t("ConnectTitle.RequiredBeneficiary")}
                />
                <AutocompleteFormComponent
                    label={t("ConnectTitle.Representative")}
                    options={filteredRepresentatives || associatedPersons}
                    renderInput={renderInputRepresentative}
                    renderOptionText={renderOptionTextRepresentative}
                    renderOption={(props: any, option: any) => renderOptionRepresentative(props, option)}
                    control={control}
                    controlName="representative"
                    error={errors.representative}
                    addElement={t("ConnectTitle.NewRepresentative")}
                    disabled={!chosenBeneficiary}
                    disabledText={t("ConnectTitle.ChooseBenefFirst")}
                    onInputChange={(e: any, value: string, reason: string) => handleRepresentativeClear(reason, value)}
                />
                {usesMultiPeriods && mode == "new" && (
                    <AutocompleteFormComponent
                        label={t("ConnectTitle.PeriodType")}
                        options={periodOptions || []}
                        renderInput={renderInputPeriodType}
                        renderOptionText={renderOptionTextPeriodType}
                        renderOption={(props: any, option: any) => renderOptionPeriodType(props, option)}
                        control={control}
                        defaultFilterOptions
                        controlName="periodType"
                        error={errors.periodType}
                        required
                        disabled={!linkedResource}
                        disabledText={
                            isBookPublisher ? t("ConnectTitle.ChooseWorkFirst") : t("ConnectTitle.ChooseTitleFirst")
                        }
                        isOptionEqualToValue={(option: TPeriod, value) => option.id === value.id}
                    />
                )}

                {otherTermsMessage.termExists ? (
                    <MessageBlock
                        type="error"
                        text={
                            isBookPublisher
                                ? watch("coveredTitles")?.length
                                    ? t("ConnectTitle.TermCoveredTitleOverlap")
                                    : t("ConnectTitle.TermExistsWork")
                                : t("ConnectTitle.TermExists")
                        }
                    />
                ) : otherTermsMessage.benefPeriods ? (
                    <MessageBlock type="warning" text={t("ConnectTitle.OtherPeriodWarning_beneficiary")} />
                ) : (
                    <></>
                )}
            </div>
            <TermCustomFields section="parties" mode={mode} />
        </div>
    );
};

export default NewTermStep;
